import {
  Activity,
  Network,
  Receipt,
  Wallet,
  Bot,
  FileSearch,
  FileText,
} from "lucide-react";

/** Per-slug visual theme for the Insights pattern covers. */
const THEMES = {
  "acceptance-math-2026": { from: "#0b1e5a", to: "#ac62ea", icon: Activity, pattern: "grid" },
  "orchestrator-decision-2026": { from: "#ac62ea", to: "#5d2d8f", icon: Network, pattern: "diag" },
  "vendor-cost-review-quarterly": { from: "#0b1e5a", to: "#1e3a8a", icon: Receipt, pattern: "grid" },
  "bnpl-wallet-mix-margin": { from: "#6d28d9", to: "#ac62ea", icon: Wallet, pattern: "dots" },
  "agentic-commerce-revenue-surface": { from: "#1a1a2e", to: "#6d28d9", icon: Bot, pattern: "diag" },
  "procurement-ai-first-orgs": { from: "#312e81", to: "#0b1e5a", icon: FileSearch, pattern: "grid" },
};

const DEFAULT_THEME = { from: "#0b1e5a", to: "#ac62ea", icon: FileText, pattern: "grid" };

const PATTERN_STYLES = {
  grid: {
    backgroundImage:
      "linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)",
    backgroundSize: "28px 28px",
  },
  diag: {
    backgroundImage:
      "repeating-linear-gradient(135deg, rgba(255,255,255,0.07) 0 1px, transparent 1px 14px)",
  },
  dots: {
    backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.14) 1px, transparent 1.5px)",
    backgroundSize: "22px 22px",
  },
};

const ICON_SIZES = { sm: "h-20 w-20", md: "h-32 w-32", lg: "h-48 w-48" };
const PADDINGS = { sm: "p-5", md: "p-5", lg: "p-8" };

/** Insights cover artwork — owns the brand voice, no stock photography. */
export default function ArticleCover({ article, className = "", size = "md" }) {
  const theme = THEMES[article?.slug] || DEFAULT_THEME;
  const Icon = theme.icon;
  const iconSize = ICON_SIZES[size] || ICON_SIZES.md;
  const padding = PADDINGS[size] || PADDINGS.md;

  return (
    <div
      className={`relative overflow-hidden article-cover-root ${className}`}
      data-testid="article-cover"
      style={{
        // Default gradient angle 135°. Shifts to ~155° on group-hover via CSS below.
        backgroundImage: `linear-gradient(var(--cover-angle, 135deg), ${theme.from} 0%, ${theme.to} 100%)`,
        transition: "background-image 600ms ease",
      }}
    >
      {/* Pattern overlay */}
      <div
        className="absolute inset-0 transition-opacity duration-500 group-hover:opacity-100 opacity-90"
        style={PATTERN_STYLES[theme.pattern]}
        aria-hidden
      />

      {/* Soft vignette */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(120% 120% at 0% 0%, rgba(0,0,0,0) 40%, rgba(0,0,0,0.35) 100%)",
        }}
        aria-hidden
      />

      {/* Watermark icon — rotates gently on parent group-hover */}
      <Icon
        className={`absolute -right-6 -bottom-6 ${iconSize} text-white/15 transition-transform duration-700 ease-out group-hover:rotate-[8deg] group-hover:scale-110 group-hover:text-white/25`}
        strokeWidth={1.2}
        aria-hidden
      />

      {/* Foreground content */}
      <div className={`relative ${padding} flex h-full flex-col justify-between`}>
        <div
          className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] font-medium text-white/85"
          data-testid="article-cover-category"
        >
          {article?.category || "Insights"}
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-md bg-white/15 backdrop-blur flex items-center justify-center text-white text-xs font-semibold transition-colors duration-300 group-hover:bg-white/25">
            S
          </div>
          <div className="text-white/80 text-xs font-medium tracking-tight">SideB</div>
        </div>
      </div>

      {/* Gradient-angle shift on group hover. Tailwind has no direct utility for
          custom-property changes, so a tiny scoped style block does it. */}
      <style>{`
        .group:hover .article-cover-root,
        .group:focus-visible .article-cover-root {
          --cover-angle: 155deg;
        }
      `}</style>
    </div>
  );
}
